import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, ChevronDown, ChevronUp } from "lucide-react";
import MaintenancePhotos from "@/components/MaintenancePhotos";
import TicketBids from "@/components/TicketBids";
import VendorRatingDialog from "@/components/VendorRatingDialog";
import TicketTimelinePanel from "@/components/TicketTimelinePanel";
import { Star } from "lucide-react";

const STATUS_COLOR = {
  open: "bg-orange-50 border-orange-200 text-orange-700",
  in_progress: "bg-indigo-50 border-indigo-200 text-indigo-700",
  resolved: "bg-emerald-50 border-emerald-200 text-emerald-700",
};

export default function MaintenancePage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Plumbing", priority: "medium", description: "" });
  const load = async () => { const { data } = await api.get("/maintenance"); setItems(data.items); };
  useEffect(() => { load(); }, []);
  const submit = async () => {
    await api.post("/maintenance", form);
    toast.success("Request submitted");
    setOpen(false); setForm({ title: "", category: "Plumbing", priority: "medium", description: "" });
    load();
  };
  const active = items.filter((m) => m.status !== "resolved");
  const past = items.filter((m) => m.status === "resolved");
  return (
    <div>
      <PageHeader
        eyebrow="Care"
        title="Maintenance"
        subtitle="Request repairs and track progress."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-maint" className="rounded-full bg-zinc-900 hover:bg-zinc-800">
                <Plus className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">New request</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
              <DialogHeader><DialogTitle>New maintenance request</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input data-testid="m-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Plumbing","HVAC","Electrical","Locksmith","Landscaping","General"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["low","medium","high","urgent"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Description</Label><Textarea data-testid="m-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
                <Button data-testid="m-submit" onClick={submit} className="w-full">Submit</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <Tabs defaultValue="active">
        <TabsList className="bg-white border h-auto p-1">
          <TabsTrigger value="active" data-testid="tab-active">Active ({active.length})</TabsTrigger>
          <TabsTrigger value="past">Past ({past.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="active" className="mt-4 space-y-3">
          {active.map((m) => <MaintenanceCard key={m.id} m={m} onUpdate={load} />)}
          {active.length === 0 && <div className="text-sm text-muted-foreground">No active requests.</div>}
        </TabsContent>
        <TabsContent value="past" className="mt-4 space-y-3">
          {past.map((m) => <MaintenanceCard key={m.id} m={m} onUpdate={load} />)}
          {past.length === 0 && <div className="text-sm text-muted-foreground">No past requests.</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MaintenanceCard({ m, onUpdate }) {
  const [rateOpen, setRateOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const canRate = m.status === "resolved" && m.assigned_vendor_id;
  return (
    <Card className="p-4 sm:p-5 bg-white" data-testid={`m-card-${m.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-widest text-zinc-500">{m.category}</div>
          <div className="text-base sm:text-lg font-semibold font-display mt-1 break-words">{m.title}</div>
          <div className="text-sm text-zinc-600 mt-1 break-words">{m.description}</div>
          {m.assigned_vendor && (
            <div className="text-xs text-emerald-700 mt-1 break-words">Assigned to {m.assigned_vendor}
              {m.accepted_bid_amount ? ` · $${Number(m.accepted_bid_amount).toFixed(2)}` : ""}</div>
          )}
        </div>
        <div className="flex flex-row sm:flex-col items-start sm:items-end gap-2 flex-wrap">
          <Badge variant="outline" className={STATUS_COLOR[m.status]}>{m.status.replace("_"," ")}</Badge>
          <Badge variant="outline" className="capitalize">{m.priority}</Badge>
          {canRate && (
            <Button
              size="sm"
              onClick={() => setRateOpen(true)}
              data-testid={`rate-${m.id}`}
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Star className="h-4 w-4 mr-1" /> Rate vendor
            </Button>
          )}
        </div>
      </div>
      <MaintenancePhotos ticket={m} editable onUpdate={onUpdate} />
      <TicketBids ticket={m} onUpdate={onUpdate} />
      <Button
        size="sm"
        variant="outline"
        onClick={() => setTimelineOpen((v) => !v)}
        className="rounded-full mt-3"
        data-testid={`timeline-toggle-${m.id}`}
      >
        {timelineOpen ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
        {timelineOpen ? "Hide" : "Show"} live timeline
      </Button>
      {timelineOpen && (
        <div className="mt-3">
          <TicketTimelinePanel ticket={m} />
        </div>
      )}
      <VendorRatingDialog open={rateOpen} onOpenChange={setRateOpen} ticket={m} onSaved={onUpdate} />
    </Card>
  );
}
